import React, { useEffect, useState } from "react";
import api from "../services/api";

const DefermentRequestForm = () => {
  const [courseToDefer, setCourseToDefer] = useState("");
  const [startDate, setStartDate] = useState("");
  const [resumptionDate, setResumptionDate] = useState("");
  const [reason, setReason] = useState("");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    let isMounted = true;

    const fetchCourses = async () => {
      try {
        const authToken = sessionStorage.getItem("token");

        if (!authToken) {
          console.log("No auth token found");
          setLoading(false);
          return;
        }

        // First get student profile to get student ID
        const profileResponse = await api({
          url: `/api/user/student/profile`,
          method: "GET",
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (profileResponse.status === 200) {
          const studentId = profileResponse.data.data.id;

          // Then fetch student's enrolled courses
          const coursesResponse = await api({
            url: `/api/student/${studentId}/courses`,
            method: "GET",
            headers: { Authorization: `Bearer ${authToken}` },
          });

          if (isMounted && coursesResponse.status === 200) {
            setCourses(coursesResponse.data.data || []);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.log("Error fetching courses:", error.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCourses();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Clear previous messages
    setMessage({ type: "", text: "" });

    if (!courseToDefer || !startDate || !resumptionDate || !reason) {
      setMessage({
        type: "error",
        text: "Please fill in all required fields before submitting.",
      });
      return;
    }

    // Validate that resumption date is after start date
    if (new Date(resumptionDate) <= new Date(startDate)) {
      setMessage({
        type: "error",
        text: "Resumption date must be after the start date.",
      });
      return;
    }

    try {
      setSubmitting(true);
      const authToken = sessionStorage.getItem("token");

      const response = await api({
        url: `/api/deferment/create`,
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
        data: {
          student_course_id: courseToDefer,
          start_date: startDate,
          resumption_date: resumptionDate,
          reason: reason,
        },
      });

      console.log(response.data);
      setMessage({
        type: "success",
        text: "Deferment request submitted successfully! You will be notified once your request is reviewed.",
      });

      // Reset form
      setCourseToDefer("");
      setStartDate("");
      setResumptionDate("");
      setReason("");
    } catch (error) {
      console.log("Error:", error);
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Error submitting application. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-8 pb-20 max-w-4xl min-h-screen">
        {loading ? (
          <div className="w-full flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">Loading courses...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-8 w-full">
            {/* Header Section */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-2xl">⏸️</span>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Course Deferment Request
                  </h1>
                  <p className="text-gray-600 text-lg">
                    Request to temporarily pause your course enrollment
                  </p>
                </div>
              </div>
            </div>

            {/* Form Section */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-purple-600 text-lg">📝</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Deferment Details
                </h2>
              </div>

              {/* Message Display */}
              {message.text && (
                <div
                  className={`p-4 rounded-lg border ${
                    message.type === "success"
                      ? "bg-green-50 border-green-200 text-green-800"
                      : "bg-red-50 border-red-200 text-red-800"
                  } mb-6`}
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      {message.type === "success" ? (
                        <svg
                          className="h-5 w-5 text-green-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="h-5 w-5 text-red-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium">{message.text}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleFormSubmit} className="space-y-6">
                {/* Course Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course to Defer *
                  </label>
                  <select
                    value={courseToDefer}
                    onChange={(e) => setCourseToDefer(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    required
                  >
                    <option value="">Select course to defer</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.course_name} - {course.branch_name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose the course you want to temporarily pause
                  </p>
                </div>

                {/* Date Selection Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Deferment Start Date *
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      When you want the deferment to begin
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Planned Resumption Date *
                    </label>
                    <input
                      type="date"
                      value={resumptionDate}
                      onChange={(e) => setResumptionDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      When you plan to resume your studies
                    </p>
                  </div>
                </div>

                {/* Reason Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Deferment *
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none"
                    placeholder="Please explain why you need to defer your course..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Provide detailed reasons for your deferment request
                  </p>
                </div>

                {/* Information Box */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-purple-600 text-sm">ℹ️</span>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-purple-900 mb-1">
                        Important Information
                      </h3>
                      <ul className="text-sm text-purple-700 space-y-1">
                        <li>• Deferment requests are subject to approval</li>
                        <li>
                          • You may need to reapply for your course upon
                          resumption
                        </li>
                        <li>
                          • Course fees and policies may change during deferment
                        </li>
                        <li>
                          • Contact academic services for specific requirements
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6 border-t border-gray-200">
                  <div className="flex gap-4 justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setCourseToDefer("");
                        setStartDate("");
                        setResumptionDate("");
                        setReason("");
                        setMessage({ type: "", text: "" });
                      }}
                      className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200 font-medium"
                    >
                      Clear Form
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-lg transition-all duration-200 font-medium shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[140px]"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Submitting...
                        </>
                      ) : (
                        "Submit Request"
                      )}
                    </button>
                  </div>
                </div>

                {/* Message Display at Bottom */}
                {message.text && (
                  <div
                    className={`p-4 rounded-lg border ${
                      message.type === "success"
                        ? "bg-green-50 border-green-200 text-green-800"
                        : "bg-red-50 border-red-200 text-red-800"
                    } mt-6`}
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        {message.type === "success" ? (
                          <svg
                            className="h-5 w-5 text-green-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="h-5 w-5 text-red-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium">{message.text}</p>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DefermentRequestForm;
